/**
 * What each 1–5 harm-severity rating means, per harm category: the text of
 * the harm taxonomy published at HARM_SCALE_URL, keyed by the incident
 * record's category prefix (`physical` → physicalSeverityDirect, …) and
 * indexed by score - 1. Shown in the incident detail's severity-pill
 * tooltips.
 *
 * The first ten categories are the published sheet verbatim (checked against
 * it 11 Oct 2026). Psychological, Epistemic and CSEA are the Arcola
 * extension of the same scale and are not on the published page yet.
 * Digital Systems & Data and Reputational have no published definitions, so
 * their pills carry no tooltip.
 */
export const HARM_SCALE_URL = 'https://airisk.mit.edu/ai-incident-tracker/harm-taxonomy';

/**
 * The categories the harm severity scale page lists, in page order, with
 * the heading each gets there. Keys are the record prefixes used by
 * HARM_SEVERITY_DEFINITIONS and the incidents API (`physicalSeverityDirect`
 * ...). Order follows the Arcola classifier's category list, which
 * interleaves the three extensions with the original ten rather than
 * appending them; any grouping of the rows is a page-level decision still
 * to be made (Simon, 2026-10-11) and is deliberately not encoded here.
 */
export const HARM_SCALE_CATEGORIES: ReadonlyArray<{ key: string; label: string }> = [
  { key: 'physical', label: 'Physical Harm' },
  { key: 'psychological', label: 'Psychological Harm' },
  { key: 'infrastructure', label: 'Infrastructure Damage' },
  { key: 'property', label: 'Property Damage' },
  { key: 'financial', label: 'Financial Loss' },
  { key: 'environmental', label: 'Environmental Damage' },
  { key: 'maliciousContent', label: 'Toxic or Malicious Content' },
  { key: 'differentialTreatment', label: 'Differential Treatment' },
  { key: 'civilRights', label: 'Human and Civil Rights' },
  { key: 'democracy', label: 'Democratic Norms' },
  { key: 'epistemic', label: 'Epistemic Harm' },
  { key: 'privacy', label: 'Privacy' },
  { key: 'csea', label: 'Child Sexual Exploitation and Abuse (CSEA)' },
];

export const HARM_SEVERITY_DEFINITIONS: Record<string, readonly string[]> = {
  physical: [
    "None to minor injuries",
    "Moderate to severe injuries, no loss of life",
    "Small-scale casualties (1-99)",
    "Mass casualties to large-scale loss of life (up to 1 million)",
    ">1 million deaths or existential threat to humanity",
  ],
  infrastructure: [
    "None to brief, localized disruption (e.g., brief traffic control outage, localized power outage)",
    "Moderate to significant local disruption (e.g., regional traffic disruptions, city-wide power outage)",
    "Widespread regional disruption to critical service failure (e.g., multiple towns lose services, hospitals unable to function)",
    "Regional to national infrastructure collapse (e.g., multiple cities without power, nationwide internet blackout)",
    "Continental to global infrastructure collapse (e.g., multiple countries without power)",
  ],
  property: [
    "None to minor damage (<$10,000)",
    "Moderate to significant damage ($10,000-$1M)",
    "Major to extensive damage ($1M-$100M)",
    "Widespread to catastrophic destruction ($100M-$10B)",
    "Extreme catastrophic to global destruction (>$10B)",
  ],
  financial: [
    "None to small direct financial loss (<$10,000)",
    "Moderate to large direct financial loss ($10,000-$1M)",
    "Major to severe direct financial loss ($1M-$100M)",
    "Extreme direct financial loss to catastrophic loss ($100M-$10B)",
    "Economic collapse to global economic collapse (>$10B)",
  ],
  environmental: [
    "No damage to minor, reversible damage",
    "Moderate, mostly reversible to significant, partially reversible damage",
    "Major, long-term effects to severe, ecosystem-level damage (e.g., endangering of species)",
    "Widespread ecosystem collapse to irreversible habitat damage (e.g., extinction of species)",
    "Continental-scale devastation to global ecological collapse",
  ],
  maliciousContent: [
    "No harmful content to moderately offensive content (e.g., minor profanity, slurs)",
    "Highly offensive content to harmful misinformation affecting thousands (e.g., hate speech, widespread false narratives)",
    "Targeted harassment to incitement to violence affecting thousands of people",
    "Large-scale coordinated attacks to mass radicalization (e.g., nationwide disinformation, societal unrest)",
    "Societal breakdown to global breakdown of truth and trust (e.g., civil wars, inability to discern facts)",
  ],
  differentialTreatment: [
    "No differential treatment to noticeable bias",
    "Clear to systemic discrimination",
    "Widespread to institutionalized discrimination",
    "Systemic oppression to apartheid-like conditions",
    "Systemic oppression at global scale",
  ],
  civilRights: [
    "No impact to limited restrictions (minor inconveniences)",
    "Significant to severe restrictions affecting thousands of people",
    "Major to widespread rights violations affecting thousands of people",
    "Systematic rights violations to near-total rights suppression affecting hundreds of thousands to millions",
    "Complete rights abolition to global human rights catastrophe affecting millions across continents",
  ],
  democracy: [
    "No impact to minor violations in democratic processes (e.g., slight irregularities, limited misinformation)",
    "Election interference and widespread misinformation campaigns",
    "Systematic voter suppression and undermining of democratic institutions",
    "Breakdown of democratic institutions and authoritarian takeover",
    "Global democratic collapse or authoritarian lock-in",
  ],
  privacy: [
    "None or contained breach of basic data",
    "Privacy breach involving identifiable data affecting up to 1000 individuals",
    "Privacy breach involving identifiable data affecting up to 1 million individuals",
    "Privacy breach affecting >1 million individuals to societal-level privacy collapse with widespread surveillance",
    "Total surveillance state or global loss of privacy",
  ],
  psychological: [
    "None to mild, transient distress (e.g., momentary frustration)",
    "Moderate to significant emotional harm affecting up to thousands (e.g., harassment distress, manipulative engagement)",
    "Serious harm to onset of clinical disorders affecting thousands (e.g., depression, PTSD, self-harm urges, suicidal ideation)",
    "Widespread trauma to population-level mental-health decline (e.g., widespread suicidal ideation, mass manipulation)",
    "Global-scale psychological devastation to mass mental-health collapse across continents",
  ],
  epistemic: [
    "None to isolated, easily corrected factual errors (e.g., one hallucinated citation)",
    "Inaccurate information to flawed decisions affecting thousands (e.g., fabricated citations, defamation-by-hallucination, medical misinfo)",
    "Systematic degradation of knowledge affecting thousands (e.g., polluted scientific record, eroded trust in expertise)",
    "Large-scale corruption of information ecosystems affecting millions (e.g., inability to distinguish true from false, mass over-reliance)",
    "Global breakdown of the shared epistemic commons (e.g., collective loss of ability to discern truth)",
  ],
  csea: [
    "None to mild, non-sexual age-inappropriate content involving children",
    "Exposure of a minor to sexual content, or sexualized synthetic content depicting fictional minors; isolated grooming attempts",
    "CSAM depicting or targeting real, identifiable children, or AI-facilitated grooming affecting multiple children",
    "Large-scale CSAM production to systems enabling abuse networks affecting hundreds to thousands of children",
    "Systemic mass-scale facilitation of child sexual exploitation (e.g., industrial-scale CSAM, contact-abuse facilitation)",
  ],
};

/** The taxonomy's wording for `score` in `categoryKey`, or '' when there is none. */
export function harmSeverityDefinition(categoryKey: string, score: number | null | undefined): string {
  if (score == null) return '';
  return HARM_SEVERITY_DEFINITIONS[categoryKey]?.[Math.round(score) - 1] ?? '';
}

/**
 * The categories whose Direct rating equals `score`, each with the wording
 * for that rating: what sits behind a "highest direct severity" headline.
 * Empty when no category's Direct rating matches it.
 */
export function categoriesAtDirectScore(
  categories: Array<{ key: string; label: string; direct: number | null }>,
  score: number,
): Array<{ label: string; definition: string }> {
  return categories
    .filter((c) => c.direct != null && Math.round(c.direct) === Math.round(score))
    .map((c) => ({ label: c.label, definition: harmSeverityDefinition(c.key, score) }));
}
